import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { EpPopupRenewalService } from "./ep-quote-declination.service";
import { Subject, forkJoin } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { EpAppStore } from "../../services/epAppStoreService/epAppStore.service";
import { PermissionsService } from "../../services/permissions/permissions.service";
import { Router } from "@angular/router";
import { EpQuotesService } from "../../services/epQuotesService/ep-quotes.service";
import { EpErrorHandlerService } from "../../services/epErrorHandlerService/epErrorHandler.service";

/**
 * @todo separate Accept Enrollment from this modal
 */
@Component({
  selector: "app-quote-declination-popup",
  templateUrl: "./ep-quote-declination.component.html",
  styleUrls: ["./ep-quote-declination.component.scss"],
  standalone: false,
})
export class EpQuoteDeclinationPopupComponent implements OnInit, OnDestroy {
  @Input() quoteType?: string;
  @Output() closePopup = new EventEmitter<boolean>();
  @Output() acceptEnrollment = new EventEmitter<boolean>();

  popupForm: FormGroup;
  terminationDate: any;
  declineRequest: any;
  private unSubscribeAll = new Subject();
  differentOptions = [];
  submitted = false;
  currentYear = new Date().getFullYear();
  groupDetail;
  agentEmail;
  header: string;
  descriptionText: string;
  continueButton: string;
  loading = false;
  rights = {
    canDeclineSubmit: this.permissionsService.canDeclineSubmit() ? true : false,
    canDeclineReview: this.permissionsService.canDeclineReview() ? true : false,
    iscanViewBrokerSpecific: this.permissionsService.canViewBrokerSpecific()
      ? true
      : false,
    isCanViewAllTermReasons: this.permissionsService.canViewAllTermReasons()
      ? true
      : false,
  };
  acknowledgeText: string;
  externalComment: any;

  constructor(
    private formBuilder: FormBuilder,
    private storeService: EpAppStore,
    private epPopupRenewalService: EpPopupRenewalService,
    private permissionsService: PermissionsService,
    private router: Router,
    private quotesService: EpQuotesService,
    private errorService: EpErrorHandlerService,
  ) {}

  ngOnInit() {
    this.popupForm = this.formBuilder.group({
      declineReason: ["", Validators.required],
      commentSection: [""],
    });

    // Subscribe to form value changes
    this.popupForm.valueChanges
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe((value) => {
        // Form value changed
      });

    this.getGroupDetail();
  }

  getGroupDetail() {
    this.loading = true;
    const { quoteId, quoteType } =
      this.storeService.select("currentQuote").value;
    if (!this.quoteType) {
      this.quoteType = quoteType;
    }

    // Accept Enrollment only needs store data — skip all decline/term API calls
    if (this.quoteType === "AcceptEnrollment") {
      this.groupDetail = {
        ...this.storeService.select("currentQuote").value,
        ...this.storeService.select("currentGroup").value,
      };
      this.setValue();
      this.loading = false;
      return;
    }

    const apiCallList = [
      this.quotesService.getQuote(quoteId),
      this.epPopupRenewalService.getTermCodes(),
    ];

    if (this.rights.canDeclineReview) {
      apiCallList.push(this.epPopupRenewalService.getDeclineRequest(quoteId));
    }

    forkJoin(apiCallList)
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe(
        ([quote, tCodes, declineRequest]) => {
          const differentOptions = [];
          this.groupDetail = {
            ...quote,
            ...this.storeService.select("currentQuote").value,
            ...this.storeService.select("currentGroup").value,
          };
          // set group number to the store, so it will update the sticky header in the next page
          this.storeService.set("currentQuote", this.groupDetail);
          if (tCodes && tCodes.termCodes) {
            tCodes.termCodes.forEach((r) => {
              r.label = this.rights.isCanViewAllTermReasons
                ? `${r.code}-${r.termReason}`
                : r.termReason;
              r.value = r.id;
              if (
                this.rights.canDeclineReview &&
                this.rights.isCanViewAllTermReasons &&
                r.internal === true
              ) {
                differentOptions.push(r);
              } else if (this.rights.canDeclineSubmit && r.external === true) {
                differentOptions.push(r);
              }
            });
          }
          this.differentOptions = differentOptions;
          this.declineRequest = declineRequest;
          this.setValue();
          this.loading = false;
        },
        () => {
          this.loading = false;
          this.errorService.push(
            "There was a problem with that action, please try again later.",
          );
          this.closePopup.emit();
        },
      );
    if (!this.rights.canDeclineReview && this.rights.canDeclineSubmit) {
      this.declineRequest = null;
    }
  }

  setValue() {
    this.terminationDate = new Date(
      <any>new Date(this.groupDetail.effectiveDate) - 1,
    );
    if (this.quoteType === "AcceptEnrollment") {
      this.header = `Accept Enrollment`;
      this.continueButton = `Accept Enrollment`;
    } else {
      if (this.rights.canDeclineReview) {
        if (this.declineRequest) {
          this.header = `Decline Renewal Request`;
          this.descriptionText = `After submitting the declination, this group will move into the <b>For Review</b> Queue.
        Terminations will take effect on the termination date listed above.`;
          this.continueButton = `Approve Request`;
          this.popupForm.controls.declineReason.setValue(
            this.declineRequest.termCodeId,
          );
          this.externalComment = this.declineRequest.comment
            ? this.declineRequest.comment
            : "N/A";
        } else {
          this.header = `Decline Renewal`;
          this.descriptionText = `After submitting a declination, this group will move into your <b>Completed queue</b>.
        Termination will not take effect until the termination date.`;
          this.continueButton = `Decline Renewal`;
        }
      } else if (this.rights.canDeclineSubmit) {
        this.header = `Request Renewal Declination`;
        this.descriptionText = `After submitting the declination, this group will move into your <b>For Review</b> Queue.
      Termination requests will be reviewed by an account manager. Once approved,
      terminations will take effect on the termination date listed above.`;
        this.continueButton = `Request Renewal Declination`;
      }
    }
  }

  ngOnDestroy() {
    this.unSubscribeAll.next();
    this.unSubscribeAll.complete();
  }

  onDeclineReasonChange(event: any): void {
    const value = event.detail?.value ?? event.target?.value ?? "";
    this.popupForm.patchValue({ declineReason: value });
  }

  onAcceptEnrollment() {
    this.acceptEnrollment.emit(true);
  }

  onButtonClick(value?) {
    if (value) {
      this.postDeclineRequest(value);
    } else {
      this.closePopup.emit(false);
    }
  }

  postDeclineRequest(value?) {
    let property;
    if (this.rights.canDeclineReview) {
      property = {
        externalComment: this.declineRequest
          ? this.declineRequest.comment
          : null,
        internalComment: this.popupForm.controls.commentSection.value,
        externalTermCodeId: this.declineRequest
          ? this.declineRequest.termCodeId
          : null,
        internalTermCodeId: this.popupForm.controls.declineReason.value,
        isDeclineRequestAccepted: value === "reject" ? false : true,
      };
    } else {
      property = {
        externalComment: this.popupForm.controls.commentSection.value,
        internalComment: null,
        externalTermCodeId: this.popupForm.controls.declineReason.value,
        internalTermCodeId: null,
        isDeclineRequestAccepted: false,
      };
    }
    this.epPopupRenewalService
      .postDeclineRequest(this.groupDetail.quoteId, property)
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe(() => {
        this.storeService.set("quoteType", {
          quoteType: "DeclineRenewal",
          quoteDate: this.terminationDate,
        });
        this.closePopup.emit(false);
        value === "reject"
          ? this.router.navigate(["/main/renewal/dashboard"])
          : this.router.navigate(["/main/renewal/as-is"]);
      });
  }
}
